import React, { useCallback, useMemo, useState } from "react";

import { setLocalStorageItem } from "~/lib/localStorage";
import { loginUser } from "~/lib/payload/users";
import { identifyUser, trackEvent } from "~/providers/AnalyticsProvider";
import { SessionContext } from "~/providers/SessionProvider";
import type { UserRole } from "~/types";
import { trackLogin } from "~/util/tracking";

/**
 * Local session provider that mimics SessionProvider but doesn't cause full rerenders
 * Used for form previews to maintain state during authentication
 */
const LocalSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<any>(null);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      role: UserRole,
      token?: string | null | undefined
    ) => {
      setIsLoading(true);

      try {
        const { data, success, message } = await loginUser(
          email,
          password,
          token
        );

        if (success && data) {
          // Update localStorage for global session sync
          setLocalStorageItem("hasAccount", true);
          setLocalStorageItem("loggedin", true);
          setLocalStorageItem("role", role);

          // Store user data in state
          setUser({ ...data.user, internalRole: role });
          setIsAuthenticated(true);

          // Track analytics
          try {
            await identifyUser({
              id: data.user.id,
              name: `${data.user.firstName} ${data.user.lastName}`,
              email: data.user.email,
              userType: role,
            });

            trackLogin(data.user.id, role);
            trackEvent("USER_LOGGED_IN");
          } catch (analyticsError) {
            console.warn("Analytics tracking failed:", analyticsError);
          }

          setIsLoading(false);
          return { success: true };
        }

        setIsLoading(false);
        return {
          success: false,
          message: message || "Authentication failed",
        };
      } catch (error) {
        setIsLoading(false);
        console.error("Sign in error:", error);
        return {
          success: false,
          message: "Network error during authentication",
        };
      }
    },
    []
  );

  const signOut = useCallback(async () => {
    // This is a local signout, not needed for form preview
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const localSession = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      isInitialLoad: false,
      isRefreshing: false,
      user,
      exp: null,
      signIn,
      signOut,
      setUnauthorized: () => {},
    }),
    [isAuthenticated, isLoading, user, signIn, signOut]
  );

  return (
    <SessionContext.Provider value={localSession}>
      {children}
    </SessionContext.Provider>
  );
};

export default LocalSessionProvider;
